/**
 * DataHygiene.tsx — Quality issues that need cleanup before pipeline review.
 * Wired to live Supabase data.
 *
 * Route: /reporting/data-hygiene
 *
 * Four issue lists:
 *   1. Accounts with no decision_makers
 *   2. Accounts with no answers in 30 days (account_answers.updated_at)
 *   3. Bucket / CP-completion mismatch
 *      - bucket = COMMIT but green count < 8, or
 *      - bucket = OMITTED but green count >= 4
 *   4. Accounts with no activity for > 14 days (max(updated_at) across CP/answers/account)
 */
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import OperatorShell from "@/components/operator/OperatorShell";
import {
  AccountRow,
  DecisionMakerRow,
  ForecastRow,
  UserRow,
  daysSince,
  fmtUSD,
  listAccountForecasts,
  listAccounts,
  listAllDecisionMakers,
  listAllUsers,
  listAnswersFor,
  listCpStatusFor,
  summarizeCpStatus,
} from "@/lib/reporting-live";
import { supabase } from "@/lib/session";

// Pull all answers in tenant (we need the latest updated_at per account, not full payload).
async function listAnswerHeartbeats(): Promise<Map<number, string>> {
  // We fetch only the account_id + updated_at for accounts in this tenant.
  // Filter via the account_id IN (...) is added by callers; here we fetch all
  // and let the caller intersect with tenant accounts.
  const { data, error } = await supabase()
    .from("account_answers")
    .select("account_id,updated_at")
    .order("updated_at", { ascending: false });
  if (error) throw error;
  const m = new Map<number, string>();
  for (const r of (data || []) as { account_id: number; updated_at: string }[]) {
    if (!m.has(r.account_id)) m.set(r.account_id, r.updated_at);
  }
  return m;
}

interface RowVM {
  account: AccountRow;
  ownerName: string;
  green: number;
  bucket: string;
  daysSinceActivity: number;
  daysSinceAnswer: number;
  hasDecisionMakers: boolean;
}

export default function DataHygiene() {
  const [, navigate] = useLocation();
  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [forecasts, setForecasts] = useState<ForecastRow[]>([]);
  const [dms, setDms] = useState<DecisionMakerRow[]>([]);
  const [cpByAccount, setCpByAccount] =
    useState<ReturnType<typeof summarizeCpStatus> | null>(null);
  const [latestAnswerByAccount, setLatestAnswerByAccount] = useState<Map<number, string>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [acc, us, fc, dm, latest] = await Promise.all([
          listAccounts(),
          listAllUsers(),
          listAccountForecasts(),
          listAllDecisionMakers(),
          listAnswerHeartbeats(),
        ]);
        if (cancelled) return;
        setAccounts(acc);
        setUsers(us);
        setForecasts(fc);
        setDms(dm);
        setLatestAnswerByAccount(latest);
        const ids = acc.map((a) => a.id);
        const cps = ids.length > 0 ? await listCpStatusFor(ids) : [];
        if (cancelled) return;
        setCpByAccount(summarizeCpStatus(cps));
        setLoading(false);
      } catch (err: any) {
        if (cancelled) return;
        setError(err?.message || "Failed to load");
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const userMap = useMemo(() => new Map(users.map((u) => [u.id, u])), [users]);
  const dmCountByAccount = useMemo(() => {
    const m = new Map<number, number>();
    dms.forEach((d) => m.set(d.account_id, (m.get(d.account_id) || 0) + 1));
    return m;
  }, [dms]);
  const forecastMap = useMemo(
    () => new Map(forecasts.map((f) => [f.account_id, f])),
    [forecasts],
  );

  const rows: RowVM[] = useMemo(() => {
    return accounts.map((a) => {
      const cp = cpByAccount?.get(a.id);
      const latestActivity = cp?.lastUpdated || a.updated_at;
      const latestAnswer = latestAnswerByAccount.get(a.id) || null;
      const owner = userMap.get(a.owner_user_id);
      const f = forecastMap.get(a.id);
      return {
        account: a,
        ownerName: owner?.name || owner?.email || `user ${a.owner_user_id}`,
        green: cp?.green || 0,
        bucket: (f?.forecast_bucket || a.bucket || "").toUpperCase(),
        daysSinceActivity: daysSince(latestActivity),
        daysSinceAnswer: daysSince(latestAnswer),
        hasDecisionMakers: (dmCountByAccount.get(a.id) || 0) > 0,
      };
    });
  }, [accounts, cpByAccount, dmCountByAccount, forecastMap, latestAnswerByAccount, userMap]);

  const noDms = rows.filter((r) => !r.hasDecisionMakers);
  const staleAnswers = rows.filter((r) => r.daysSinceAnswer > 30);
  const bucketMismatch = rows.filter(
    (r) => (r.bucket === "COMMIT" && r.green < 8) || (r.bucket === "OMITTED" && r.green >= 4),
  );
  const stale14d = rows.filter((r) => r.daysSinceActivity > 14);

  return (
    <OperatorShell breadcrumbs={[{ label: "Reporting", href: "/reporting" }, { label: "Data Hygiene" }]}>
      <div style={{ maxWidth: 1180, margin: "0 auto" }}>
        <a
          href="/reporting"
          style={{ fontSize: 12, color: "#6B6B70", textDecoration: "none", fontWeight: 500 }}
        >
          ← Reporting Hub
        </a>
        <h1 style={{ fontSize: 24, fontWeight: 900, color: "#0B0B0F", margin: "10px 0 4px" }}>
          Data Hygiene
        </h1>
        <div style={{ fontSize: 13, color: "#6B6B70", marginBottom: 20 }}>
          What to clean up before the next pipeline review.
        </div>

        {/* CRM sync status — placeholder while CRM integration is in pilot setup. */}
        <CrmSyncTile accountCount={accounts.length} />

        {error && (
          <div
            style={{
              padding: 12,
              background: "#FEF2F2",
              border: "1px solid #FCA5A5",
              borderRadius: 8,
              color: "#991B1B",
              fontSize: 13,
              marginBottom: 16,
            }}
          >
            {error}
          </div>
        )}

        {/* Summary strip */}
        <div
          style={{
            background: "#0B0B0F",
            borderRadius: 12,
            padding: "16px 24px",
            marginBottom: 24,
            display: "flex",
            gap: 28,
            flexWrap: "wrap",
          }}
        >
          <Stat label="No decision makers" value={noDms.length} color="#FCA5A5" />
          <Stat label="Stale answers (30d+)" value={staleAnswers.length} color="#FCA5A5" />
          <Stat label="Bucket mismatch" value={bucketMismatch.length} color="#FDE68A" />
          <Stat label="No activity (14d+)" value={stale14d.length} color="#FCA5A5" />
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#8E8E94" }}>Loading…</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
            <IssueList
              title="Accounts with no decision makers"
              subtitle="Map at least one decision maker for every account. No DM means no path to close."
              rows={noDms}
              colLabel="DMs"
              colValue={() => "0"}
              colColor={() => "#DC2626"}
              navigate={navigate}
            />
            <IssueList
              title="No discovery answers updated in 30 days"
              subtitle="If discovery has not advanced in a month, the account is dormant. Re-engage or demote."
              rows={staleAnswers}
              colLabel="Last answer"
              colValue={(r) =>
                Number.isFinite(r.daysSinceAnswer) ? `${r.daysSinceAnswer}d` : "never"
              }
              colColor={() => "#DC2626"}
              navigate={navigate}
            />
            <IssueList
              title="Bucket does not match CP completion"
              subtitle="COMMIT with < 8 green CPs, or OMITTED with ≥ 4 green CPs. The forecast does not match the work."
              rows={bucketMismatch}
              colLabel="Bucket / Green"
              colValue={(r) => `${r.bucket} · ${r.green}/10`}
              colColor={() => "#CA8A04"}
              navigate={navigate}
            />
            <IssueList
              title="No activity in 14 days"
              subtitle="No CP, answer, or account update for two weeks. The deal is stale."
              rows={stale14d}
              colLabel="Last activity"
              colValue={(r) =>
                Number.isFinite(r.daysSinceActivity) ? `${r.daysSinceActivity}d` : "never"
              }
              colColor={() => "#DC2626"}
              navigate={navigate}
            />
          </div>
        )}
      </div>
    </OperatorShell>
  );
}

function IssueList({
  title,
  subtitle,
  rows,
  colLabel,
  colValue,
  colColor,
  navigate,
}: {
  title: string;
  subtitle: string;
  rows: RowVM[];
  colLabel: string;
  colValue: (r: RowVM) => string;
  colColor: (r: RowVM) => string;
  navigate: (to: string) => void;
}) {
  return (
    <div
      style={{
        background: "#FFFFFF",
        border: "1px solid #E5E5E7",
        borderRadius: 12,
        overflow: "hidden",
      }}
    >
      <div style={{ padding: "16px 20px", borderBottom: "1px solid #EFEFEE" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 8,
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 800, color: "#0B0B0F" }}>{title}</div>
          <div
            style={{
              padding: "2px 10px",
              borderRadius: 4,
              background: rows.length > 0 ? "#FEF2F2" : "#F0FDF4",
              color: rows.length > 0 ? "#991B1B" : "#166534",
              fontSize: 11,
              fontWeight: 700,
            }}
          >
            {rows.length}
          </div>
        </div>
        <div style={{ fontSize: 12, color: "#8E8E94", marginTop: 2 }}>{subtitle}</div>
      </div>
      {rows.length === 0 ? (
        <div style={{ padding: 16, color: "#8E8E94", fontSize: 13, textAlign: "center" }}>
          Clean.
        </div>
      ) : (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "#FAFAF7" }}>
              {["Account", "Owner AE", "Value", colLabel].map((h) => (
                <th
                  key={h}
                  style={{
                    padding: "8px 14px",
                    textAlign: "left",
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: "#8E8E94",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 50).map((r) => (
              <tr
                key={r.account.id}
                onClick={() => navigate(`/account/${r.account.id}`)}
                style={{ cursor: "pointer", borderTop: "1px solid #EFEFEE" }}
                onMouseEnter={(e) =>
                  ((e.currentTarget as HTMLElement).style.background = "#FAFAF7")
                }
                onMouseLeave={(e) =>
                  ((e.currentTarget as HTMLElement).style.background = "#FFFFFF")
                }
              >
                <td style={{ padding: "8px 14px", color: "#0B0B0F", fontWeight: 600 }}>
                  {r.account.name}
                </td>
                <td style={{ padding: "8px 14px", color: "#525258" }}>{r.ownerName}</td>
                <td style={{ padding: "8px 14px", color: "#0B0B0F" }}>
                  {fmtUSD(r.account.est_arr || 0)}
                </td>
                <td style={{ padding: "8px 14px", color: colColor(r), fontWeight: 700 }}>
                  {colValue(r)}
                </td>
              </tr>
            ))}
            {rows.length > 50 && (
              <tr>
                <td
                  colSpan={4}
                  style={{ padding: "8px 14px", color: "#8E8E94", fontSize: 12, textAlign: "center" }}
                >
                  And {rows.length - 50} more.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

/**
 * CrmSyncTile — placeholder tile that shows where CRM-sync visibility will live
 * once Salesforce / HubSpot / MS Dynamics are wired.
 *
 * Until then the tile reads "Pending pilot connection" and the counts are
 * hard zeros, with the live `accountCount` so the operator sees real scale.
 */
function CrmSyncTile({ accountCount }: { accountCount: number }) {
  const items: { label: string; value: string }[] = [
    { label: "Last sync", value: "Pending pilot connection" },
    { label: "Accounts synced", value: `0 of ${accountCount || 41}` },
    { label: "Write-backs (24h)", value: "0" },
    { label: "Conflicts", value: "0" },
  ];
  return (
    <div
      data-tour="crm-card"
      style={{
        background: "#FFFFFF",
        border: "1px solid #E5E7EB",
        borderRadius: 12,
        padding: "16px 20px",
        marginBottom: 16,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          marginBottom: 8,
        }}
      >
        <div>
          <div style={{ fontSize: 15, fontWeight: 800, color: "#0B0B0F" }}>CRM sync status</div>
          <div style={{ fontSize: 12, color: "#8E8E94", marginTop: 2 }}>
            Salesforce, HubSpot, and Microsoft Dynamics — read-and-write sync is configured per pilot.
          </div>
        </div>
        <span
          className="inline-flex items-center gap-1.5 rounded-full font-semibold uppercase"
          style={{
            background: "#EFEFEE",
            color: "#525258",
            fontSize: 11,
            padding: "4px 10px",
            letterSpacing: "0.06em",
          }}
        >
          <span style={{ width: 6, height: 6, borderRadius: 999, background: "#8E8E94" }} />
          Disconnected
        </span>
      </div>
      <div
        style={{
          fontSize: 12,
          color: "#525258",
          lineHeight: 1.5,
          marginBottom: 12,
          paddingBottom: 12,
          borderBottom: "1px dashed #E5E7EB",
        }}
      >
        Your CRM is the ledger. Attilius is the playbook. Attilius reads accounts, contacts, opportunities, owners, and stage history from the CRM. Coaching outcomes, checkpoint score deltas, and completed touches write back — opt-in per field, audited, reversible.
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          gap: 12,
        }}
      >
        {items.map((it) => (
          <div
            key={it.label}
            style={{
              background: "#FAFAF7",
              borderRadius: 8,
              padding: "10px 12px",
              border: "1px solid #EFEFEE",
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "#8E8E94",
                marginBottom: 4,
              }}
            >
              {it.label}
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#0B0B0F" }}>{it.value}</div>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 12, fontSize: 12, color: "#6B6B70" }}>
        <a
          href="/settings/integrations"
          style={{ color: "#0B0B0F", fontWeight: 600, textDecoration: "underline" }}
        >
          Configure in Settings → Integrations
        </a>
      </div>
    </div>
  );
}

function Stat({ label, value, color }: { label: string; value: string | number; color: string }) {
  return (
    <div>
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.1em",
          textTransform: "uppercase",
          color: "#525258",
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 22, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
    </div>
  );
}

